import React, {useEffect, useMemo, useState} from "react";
import Switch from "react-bootstrap-switch";

const normalizeCollection = value => {
    if (Array.isArray(value))
        return value;

    if (value && Array.isArray(value["hydra:member"]))
        return value["hydra:member"];

    return [];
};

const relationId = value => {
    if (!value)
        return "";

    if (typeof value === "string") {
        const parts = value.split("/");
        return parts[parts.length - 1];
    }

    return value.id || "";
};

const timeValue = value => {
    if (!value)
        return "";

    if (typeof value === "string")
        return value.substring(0, 5);

    return "";
};

const dayNumber = day => {
    if (typeof day.dayNumber === "number")
        return day.dayNumber;

    if (typeof day.day_number === "number")
        return day.day_number;

    return 99;
};

const dayName = day =>
    day.name ||
    day.dayName ||
    day.day_name ||
    day.id ||
    "";

const SpaStaffConfiguration = ({
    api,
    employee
}) => {
    const [loading, setLoading] = useState(false);
    const [spaStaff, setSpaStaff] = useState(null);
    const [spaEnabled, setSpaEnabled] = useState(false);
    const [shifts, setShifts] = useState([]);
    const [days, setDays] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    const employeeId = employee && employee.id
        ? employee.id
        : null;

    const orderedDays = useMemo(
        () => [...days].sort(
            (a, b) => dayNumber(a) - dayNumber(b)
        ),
        [days]
    );

    const load = async () => {
        if (!employeeId)
            return;

        setLoading(true);
        setMessage("");
        setError("");

        try {
            const [
                staffResponse,
                shiftsResponse,
                daysResponse
            ] = await Promise.all([
                api.spaStaffs.get({
                    filters: {
                        employee: employeeId
                    },
                    customProp: "employeeSpaStaff"
                }),
                api.spaShifts.get({
                    filters: {
                        active: true,
                        pagination: false
                    },
                    customProp: "employeeSpaShifts"
                }),
                api.daysOfTheWeek.get({
                    filters: {
                        pagination: false
                    },
                    customProp: "employeeSpaDays"
                })
            ]);

            const staffItems = normalizeCollection(staffResponse);
            const shiftItems = normalizeCollection(shiftsResponse);
            const dayItems = normalizeCollection(daysResponse);

            const currentStaff = staffItems.length
                ? staffItems[0]
                : null;

            setSpaStaff(currentStaff);
            setSpaEnabled(
                Boolean(
                    currentStaff &&
                    currentStaff.active !== false
                )
            );
            setShifts(shiftItems);
            setDays(dayItems);

            if (currentStaff) {
                const scheduleResponse =
                    await api.spaStaffSchedules.get({
                        filters: {
                            staff: currentStaff.id,
                            active: true,
                            pagination: false
                        },
                        customProp: "employeeSpaSchedules"
                    });

                setSchedules(
                    normalizeCollection(scheduleResponse)
                );
            } else {
                setSchedules([]);
            }
        } catch (e) {
            setError(
                "No fue posible cargar la configuración SPA."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeId]);

    const scheduleForDay = day => {
        const id = relationId(day);

        return schedules.find(
            schedule =>
                relationId(schedule.dayOfTheWeek) === id
        ) || null;
    };

    const updateLocalSchedule = (day, property, value) => {
        const dayId = relationId(day);
        const current = scheduleForDay(day);

        if (current) {
            setSchedules(
                schedules.map(schedule =>
                    relationId(schedule.dayOfTheWeek) === dayId
                        ? {
                            ...schedule,
                            [property]: value
                        }
                        : schedule
                )
            );

            return;
        }

        setSchedules([
            ...schedules,
            {
                id: null,
                dayOfTheWeek: day,
                shift: null,
                isWorking: true,
                mealStartTime: null,
                active: true,
                [property]: value
            }
        ]);
    };

    const saveSpaStatus = async value => {
        if (!employeeId || loading)
            return;

        setLoading(true);
        setMessage("");
        setError("");

        try {
            if (spaStaff) {
                await api.spaStaffs.edit({
                    id: spaStaff.id,
                    data: {
                        employee: `/api/employees/${employeeId}`,
                        active: Boolean(value)
                    },
                    customProp: "employeeSpaStaff"
                });

                setSpaStaff({
                    ...spaStaff,
                    active: Boolean(value)
                });
            } else if (value) {
                const created = await api.spaStaffs.create({
                    data: {
                        employee: `/api/employees/${employeeId}`,
                        active: true
                    },
                    customProp: "employeeSpaStaff"
                });

                setSpaStaff(created);
            }

            setSpaEnabled(Boolean(value));
            setMessage(
                value
                    ? "Personal habilitado para SPA."
                    : "Personal deshabilitado para SPA."
            );

            if (value)
                await load();
        } catch (e) {
            setError(
                "No fue posible actualizar el estado SPA."
            );
        } finally {
            setLoading(false);
        }
    };

    const saveSchedules = async () => {
        if (!spaStaff || !spaEnabled || loading)
            return;

        setLoading(true);
        setMessage("");
        setError("");

        try {
            for (const day of orderedDays) {
                const schedule = scheduleForDay(day);

                if (!schedule)
                    continue;

                const shiftId = relationId(schedule.shift);

                const data = {
                    staff: `/api/spa_staffs/${spaStaff.id}`,
                    dayOfTheWeek:
                        `/api/day_of_the_weeks/${relationId(day)}`,
                    shift: shiftId
                        ? `/api/spa_shifts/${shiftId}`
                        : null,
                    isWorking: Boolean(schedule.isWorking),
                    mealStartTime:
                        schedule.isWorking &&
                        schedule.mealStartTime
                            ? schedule.mealStartTime
                            : null,
                    active: true
                };

                if (schedule.id) {
                    await api.spaStaffSchedules.edit({
                        id: schedule.id,
                        data,
                        customProp: "employeeSpaSchedules"
                    });
                } else {
                    await api.spaStaffSchedules.create({
                        data,
                        customProp: "employeeSpaSchedules"
                    });
                }
            }

            setMessage("Horario SPA actualizado.");
            await load();
        } catch (e) {
            setError(
                "No fue posible guardar el horario SPA."
            );
        } finally {
            setLoading(false);
        }
    };

    if (!employeeId)
        return null;

    return (
        <div
            className="panel panel-default"
            style={{marginTop: 25}}
        >
            <div className="panel-heading">
                <strong>
                    <i
                        className="fa fa-user-md"
                        style={{marginRight: 8}}
                    />
                    Configuración SPA
                </strong>
            </div>

            <div className="panel-body">
                {message &&
                    <div className="alert alert-success">
                        {message}
                    </div>
                }

                {error &&
                    <div className="alert alert-danger">
                        {error}
                    </div>
                }

                <div className="row">
                    <div className="col-sm-6">
                        <label>Personal SPA</label>

                        <div>
                            <Switch
                                value={spaEnabled}
                                onText="Sí"
                                offText="No"
                                disabled={loading}
                                onChange={(sw, value) =>
                                    saveSpaStatus(value)
                                }
                            />

                            <span
                                style={{
                                    marginLeft: 10,
                                    fontWeight: 600
                                }}
                            >
                                {spaEnabled
                                    ? "Habilitado"
                                    : "No habilitado"}
                            </span>
                        </div>

                        <p className="help-block">
                            Habilita al colaborador para ser
                            seleccionado en servicios SPA que
                            requieran personal.
                        </p>
                    </div>
                </div>

                {spaEnabled &&
                    <div style={{marginTop: 20}}>
                        <h5>
                            <strong>
                                Disponibilidad semanal
                            </strong>
                        </h5>

                        <div className="table-responsive">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>Día</th>
                                        <th>Trabaja</th>
                                        <th>Turno SPA</th>
                                        <th>
                                            Inicio de comida
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {orderedDays.map(day => {
                                        const schedule =
                                            scheduleForDay(day);

                                        const working =
                                            schedule
                                                ? schedule.isWorking
                                                    !== false
                                                : false;

                                        return (
                                            <tr key={relationId(day)}>
                                                <td>
                                                    {dayName(day)}
                                                </td>

                                                <td>
                                                    <Switch
                                                        value={working}
                                                        onText="Sí"
                                                        offText="No"
                                                        disabled={loading}
                                                        onChange={
                                                            (sw, value) =>
                                                                updateLocalSchedule(
                                                                    day,
                                                                    "isWorking",
                                                                    value
                                                                )
                                                        }
                                                    />
                                                </td>

                                                <td>
                                                    <select
                                                        className="form-control"
                                                        disabled={
                                                            loading ||
                                                            !working
                                                        }
                                                        value={
                                                            schedule
                                                                ? relationId(
                                                                    schedule.shift
                                                                )
                                                                : ""
                                                        }
                                                        onChange={event =>
                                                            updateLocalSchedule(
                                                                day,
                                                                "shift",
                                                                event.target.value
                                                                    ? {
                                                                        id:
                                                                            event
                                                                                .target
                                                                                .value
                                                                    }
                                                                    : null
                                                            )
                                                        }
                                                    >
                                                        <option value="">
                                                            Seleccionar...
                                                        </option>

                                                        {shifts.map(shift => (
                                                            <option
                                                                key={shift.id}
                                                                value={shift.id}
                                                            >
                                                                {shift.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>

                                                <td>
                                                    <input
                                                        type="time"
                                                        className="form-control"
                                                        disabled={
                                                            loading ||
                                                            !working
                                                        }
                                                        value={
                                                            schedule
                                                                ? timeValue(
                                                                    schedule
                                                                        .mealStartTime
                                                                )
                                                                : ""
                                                        }
                                                        onChange={event =>
                                                            updateLocalSchedule(
                                                                day,
                                                                "mealStartTime",
                                                                event.target.value
                                                                    || null
                                                            )
                                                        }
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="text-right">
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={loading}
                                onClick={saveSchedules}
                            >
                                <i
                                    className="fa fa-save"
                                    style={{marginRight: 7}}
                                />
                                Guardar configuración SPA
                            </button>
                        </div>
                    </div>
                }
            </div>
        </div>
    );
};

export default SpaStaffConfiguration;
